import React, { useEffect, useRef, useState } from "react";
import api from "../../api/axiosInstance";

const fieldClass =
  "relative block w-full appearance-none rounded-lg border border-lap-hairline bg-lap-bg/70 px-3.5 py-3 text-white placeholder-lap-dim transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-lap-accent sm:text-sm";

/** Scroll-the-doc + typed name. Parent submits `{document_id, version, sha256, typed_name}`. */
export function LegalAgreementStep({
  onSigned,
  submitLabel = "Agree and continue",
  submitting = false,
}) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [read, setRead] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [typedName, setTypedName] = useState("");
  const scroller = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/auth/legal")
      .then((res) => {
        if (!cancelled) setDoc(res.data);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load the terms. Refresh and try again.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el || !doc) return;
    if (el.scrollHeight <= el.clientHeight + 8) setRead(true);
  }, [doc]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 16) setRead(true);
  };

  const canSubmit = Boolean(doc && read && agreed && typedName.trim().length >= 2 && !submitting);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSigned({
      document_id: doc.id,
      version: doc.version,
      sha256: doc.sha256,
      typed_name: typedName.trim(),
    });
  };

  if (error) {
    return <p className="text-sm text-lap-loss">{error}</p>;
  }
  if (!doc) {
    return <p className="text-sm text-lap-muted">Loading terms…</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="legal-agreement">
      <div>
        <h3 className="text-lg font-bold text-white">{doc.title}</h3>
        <p className="mt-1 text-xs text-lap-dim">Version {doc.version}. Scroll to the end, then sign.</p>
      </div>
      <div
        ref={scroller}
        onScroll={onScroll}
        data-testid="legal-scroller"
        className="h-48 overflow-y-auto rounded-lg border border-lap-hairline bg-lap-bg/70 px-4 py-3 text-sm text-lap-text whitespace-pre-wrap sm:h-64"
      >
        {doc.body.replace(/^<!--.*?-->\n/, "").replace(/^# [^\n]+\n+/, "")}
      </div>
      <label className="flex items-start gap-3 text-sm text-lap-text">
        <input
          type="checkbox"
          className="mt-1"
          checked={agreed}
          disabled={!read}
          onChange={(e) => setAgreed(e.target.checked)}
          data-testid="legal-agree"
        />
        <span>
          I have read this document and agree to the Terms of Use and Liability Waiver.
          {!read && <span className="block text-xs text-lap-dim">Scroll to the end to enable this.</span>}
        </span>
      </label>
      <div>
        <label htmlFor="legal-typed-name" className="block text-sm font-medium text-lap-text mb-2">
          Type your full name to sign
          <span className="text-lap-accent-bright" aria-hidden="true">
            {" *"}
          </span>
        </label>
        <input
          id="legal-typed-name"
          data-testid="legal-typed-name"
          type="text"
          autoComplete="name"
          className={fieldClass}
          placeholder="Your legal name"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
        />
      </div>
      <button
        type="submit"
        disabled={!canSubmit}
        data-testid="legal-submit"
        className="flex w-full justify-center rounded-lg bg-lap-accent px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-lap-pressed disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? "Saving…" : submitLabel}
      </button>
    </form>
  );
}
